<template>
  <div class="auth-root pos-relative o-hidden">
    <!-- Шейдер — фон на всё окно (как было до сплита). -->
    <Shader
      class="pos-absolute pos-t-0 pos-l-0 w-100 h-100 z-index-0"
      :theme="ds.theme.mode"
      :color="accent"
      shader="
        precision mediump float;
        uniform vec2 iResolution;
        uniform float iTime;
        uniform float iTheme;
        uniform vec3 iColor;

        void main() {
          float mr = min(iResolution.x, iResolution.y);
          vec2 uv = (gl_FragCoord.xy * 2.0 - iResolution.xy) / mr;

          uv.x = -uv.x;

          float d = -iTime * 0.5;
          float a = 0.0;
          for (float i = 0.0; i < 8.0; ++i) {
            a += cos(i - d - a * uv.x);
            d += sin(uv.y * i + a);
          }
          d += iTime * 0.5;
          vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
          col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5);

          float intensity = (col.r + col.g + col.b) / 3.0;

          // Оба полюса градиента считаются от акцента темы: светлый — сам
          // акцент, тёмный — он же приглушённый. Литералов цвета здесь нет,
          // иначе экран входа остаётся синим у проекта с любой другой темой.
          vec3 c1;
          vec3 c2;

          if (iTheme > 0.5) {
            c1 = iColor;
            c2 = iColor * 0.24;
          } else {
            c1 = iColor * 0.7;
            c2 = iColor * 0.18;
          }

          col = mix(c1, c2, intensity);

          gl_FragColor = vec4(col, 1.0);
        }
      "
    />

    <!-- Контент поверх шейдера. Единый паддинг до краёв экрана + зазор между
         панелью и карточкой. Есть панель → слева панель, справа карточка;
         нет панели → карточка по центру. -->
    <div class="auth-content" :class="{ 'auth-content--solo': !aside }">
      <div v-if="aside" class="auth-content__aside desktop-only">
        <component :is="aside" />
      </div>

      <div class="auth-content__form">
        <div class="auth-card bg-base pd-big mobile:pd-medium w-100 w-max-30r">
          <router-view v-slot="{ Component, route: r }">
            <transition name="scaleIn" mode="out-in">
              <component
                ref="page"
                :key="r.path"
                :localPosition="localPosition"
                :is="Component"
              />
            </transition>
          </router-view>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import Shader from '@martyrs/src/design-system/components/Shader/Shader.vue'
import { ds } from '@martyrs/src/design-system/index.js'

const route = useRoute()

// Цвет фона берётся из темы проекта живьём: ds.token читает значение с <html>,
// поэтому после смены темы пересчитывается вместе с ds.theme.mode.
const accent = computed(() => {
  // Обращение к режиму темы ставит его в зависимости: после переключения токен
  // читается заново, и фон меняется вместе с темой.
  ds.theme.mode
  return ds.token('color-accent')
})

// Маркетинговая панель — из меты роута (проект кладёт свой компонент).
const aside = computed(() => route.meta?.auth_aside || null)
</script>

<style lang="scss">
.auth-root {
  min-height: 100vh;
}

.auth-content {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: var(--big);
  padding: var(--big);
  align-items: stretch;

  &--solo {
    grid-template-columns: 1fr;
  }

  // Панель растянута на всю высоту (кнопки прижаты к низу).
  &__aside {
    position: relative;
  }

  // Правая колонка: карточка тянется на всю высоту (сравнивается по низу со
  // слайдером), центрируется по горизонтали.
  &__form {
    display: flex;
    align-items: stretch;
    justify-content: center;
  }
}

// Карточка на всю высоту, контент по верхнему паддингу — высота не зависит от
// количества полей, поэтому при переходе между роутами карточка не скачет.
.auth-card {
  // Радиус — рамка поверхностей проекта, своего значения карточка не держит:
  // на телефоне тема обнуляет его вместе с полем страницы, и карточка ложится
  // от края до края, как остальные поверхности. Фолбэк — прежний `--big`.
  --radius-auth-card: var(--radius-surface, var(--big));
  border-radius: var(--radius-auth-card);
  align-self: stretch;
}

// Вложенным поверхностям — радиус изнутри: радиус карточки минус её паддинг,
// не ниже нижней границы. Паддинг карточки здесь `--big`. Считается от
// --radius-auth-card карточки: `--radius-surface: calc(var(--radius-surface) …)`
// ссылается на саму себя, такая переменная недействительна, и радиус у
// вложенных поверхностей был 0.
.auth-card > * {
  --radius-surface: max(var(--radius-min, var(--small)), calc(var(--radius-auth-card) - var(--big)));
}

.auth-card {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

// Форма-секция занимает всю высоту карточки, чтобы прибитая mn-t-auto ссылка
// («Нет аккаунта?» / «У вас уже есть аккаунт?») уезжала к низу карточки.
.auth-card > section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

// Единая высота контролов на экране входа: инпут (.input) и кнопка (.button)
// одного размера. Локально — чтобы не трогать общие .input--l/.button--l,
// которые используются десятками в других модулях.
.auth-card .input,
.auth-card .button--accent {
  height: 3.25rem;
}

// Мобильные: одна колонка, панель скрыта, карточка 100% ширины с паддингом по краям.
@media (max-width: 1024px) {
  .auth-content {
    grid-template-columns: 1fr;
    // Поле — рамка страницы проекта: где оно обнулено, карточка входа ложится
    // от края до края вместе с её радиусом.
    padding: var(--page-gutter-block, var(--medium)) var(--page-gutter, var(--medium));
  }
  .auth-card {
    max-width: none;
  }
}
</style>
